import { HomeIcon, StoreIcon } from "@/components/icons";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export function StoreScreen() {
  const navigate = useNavigate();
  const { username } = useAuth();

  // Placeholder data for store items
  const storeItems = [
    {
      id: "checker_red",
      name: "Red Checker Set",
      description: "Classic red checker pieces with glossy finish",
      price: 100,
      currency: "coins",
      image: "🔴",
      category: "game_pieces"
    },
    {
      id: "checker_blue",
      name: "Blue Checker Set",
      description: "Ocean blue checker pieces with matte finish",
      price: 100,
      currency: "coins",
      image: "🔵",
      category: "game_pieces"
    },
    {
      id: "board_wood",
      name: "Wooden Board",
      description: "Traditional wooden board with rich grain patterns",
      price: 250,
      currency: "coins",
      image: "🪵",
      category: "boards"
    },
    {
      id: "board_marble",
      name: "Marble Board",
      description: "Luxury marble playing surface with polished finish",
      price: 500,
      currency: "coins",
      image: "🏛️",
      category: "boards"
    },
    {
      id: "dice_gold",
      name: "Golden Dice",
      description: "Premium dice with golden accents",
      price: 150,
      currency: "coins",
      image: "🎲",
      category: "dice"
    }
  ];

  // Group items by category for carousel sections
  const groupedItems = storeItems.reduce(
    (acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    },
    {} as Record<string, typeof storeItems>
  );

  const categoryNames = {
    game_pieces: "Game Pieces",
    boards: "Game Boards",
    dice: "Dice Sets"
  };

  return (
    <div className="w-full h-screen overflow-hidden bg-gradient-to-b from-emerald-900 to-teal-900 text-white relative flex flex-col">
      {/* Fixed Navigation and Currency */}
      <div className="fixed top-4 left-4 z-10">
        <button
          onClick={() => navigate("/")}
          className="w-12 h-12 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full flex items-center justify-center text-white transition-all"
        >
          <HomeIcon size={20} />
        </button>
      </div>

      <div className="fixed top-4 right-4 z-10 flex items-center gap-3">
        <div className="flex items-center bg-black bg-opacity-30 px-3 py-2 rounded-full">
          <span className="text-yellow-400 mr-2">💰</span>
          <span className="font-bold text-sm">0</span>
        </div>
        <button className="bg-yellow-600 hover:bg-yellow-500 px-6 py-3 rounded-full text-white text-sm font-medium transition-colors shadow-lg">
          Buy Coins
        </button>
      </div>

      {/* Store Categories with Horizontal Carousels */}
      <div
        className="flex-1 overflow-y-auto px-6 space-y-6 pt-20 pb-6"
        style={{ overscrollBehaviorY: "contain" }}
      >
        {Object.entries(groupedItems).map(([category, items]) => (
          <div key={category}>
            <h2 className="text-lg font-bold mb-3 flex items-center">
              <StoreIcon size={20} className="mr-2" />
              {categoryNames[category as keyof typeof categoryNames]}
            </h2>

            {/* Horizontal scrolling carousel */}
            <div className="overflow-x-auto -mx-6 px-6">
              <div className="flex gap-3 pb-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-emerald-800 bg-opacity-70 rounded-lg p-4 shadow-lg flex-shrink-0 w-72 first:ml-0 last:mr-6"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-lg bg-emerald-700 flex items-center justify-center text-2xl flex-shrink-0">
                        {item.image}
                      </div>
                      <div className="flex-grow min-w-0">
                        <h3 className="font-bold text-base mb-1 truncate">{item.name}</h3>
                        <p className="text-emerald-300 text-xs mb-3 line-clamp-2">
                          {item.description}
                        </p>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="text-yellow-400 mr-1">💰</span>
                            <span className="font-bold text-sm">{item.price}</span>
                          </div>
                          <button className="bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded text-white text-xs font-medium transition-colors min-h-[44px] min-w-[44px]">
                            Buy
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Coming Soon Section */}
        <div className="bg-emerald-800 bg-opacity-50 rounded-lg p-4">
          <h3 className="text-lg font-bold mb-2 flex items-center">
            <span className="mr-2">🎨</span>
            Coming Soon
          </h3>
          <p className="text-emerald-300 text-sm">
            More customization options, premium themes, and exclusive seasonal items!
          </p>
        </div>
      </div>
    </div>
  );
}

export default StoreScreen;
